<script setup lang="ts">
import { computed } from 'vue';
import ScoreRating from '@/components/Conversations/Application/ScoreRating.vue';
import ScoreChip from '@/components/Conversations/Application/ScoreChip.vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import CopilotScoreFeedback from '@/components/Conversations/Copilot/CopilotScoreFeedback.vue';
import MeService from '@/core/shared/me/me.service';
import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import {
  hasApplicationScore,
  isApplicationNotSourcedByAiRecruiter,
} from '@/core/conversations/application-score/application-score.util';
import { renderScoreMarkdown } from '@/utils/strings/render-score-markdown.util';

const props = withDefaults(
  defineProps<{
    scoreDetails: ATSApplicationScoringProScoring | undefined;
    jobTitle: string;
    jobId?: number;
    isLoading: boolean;
    analyticsSource: string;
    applicationId: number;
    hasLoadError?: boolean;
    initiatedByOutreach?: boolean;
    initiatedByCopilot?: boolean;
  }>(),
  {
    isLoading: true,
    hasLoadError: false,
  },
);

const meService = new MeService();
const userProfileId = computed(() => meService.userProfile?.id);
const hasScore = computed(() => hasApplicationScore(props.scoreDetails?.score));
const scoreValue = computed<number | undefined>(() =>
  hasScore.value ? (props.scoreDetails?.score as number) : undefined,
);
const normalizedScore = computed(() =>
  hasScore.value ? ApplicationScoreService.formatScore(props.scoreDetails?.score) : '',
);
const hasSummary = computed(() => Boolean(props.scoreDetails?.summary));
const isLegacyScore = computed(() => hasScore.value && !hasSummary.value && !props.hasLoadError);
const isNotScored = computed(() => !hasScore.value);
const isScoreUnavailable = computed(() => isNotScored.value && props.hasLoadError);
const isPendingScore = computed(() => isNotScored.value && !props.hasLoadError);
const scoreVersion = computed(() => ApplicationScoreService.getScoreVersion(props.scoreDetails));
const chips = computed(() => ApplicationScoreService.getScoreChipsInfo(props.scoreDetails));
const isNotSourcedByAiRecruiter = computed(() =>
  isApplicationNotSourcedByAiRecruiter(props.initiatedByOutreach, props.initiatedByCopilot),
);

const summaryText = computed(() => {
  if (isLegacyScore.value) {
    return 'Summary unavailable. This candidate applied before the release of the summary tool.';
  }
  if (isScoreUnavailable.value) {
    // eslint-disable-next-line max-len
    return 'We were unable to get the score for this candidate. If this keeps happening, please reach out to support.';
  }
  if (isPendingScore.value) {
    return "This candidate may have been moved from another job or hasn't been scored for this job yet.";
  }
  if (props.hasLoadError) {
    // eslint-disable-next-line max-len
    return 'We were unable to load the score explanation for this candidate. If this keeps happening, please reach out to support.';
  }
  return props.scoreDetails?.summary || '';
});

const summaryHtml = computed(() => renderScoreMarkdown(summaryText.value));
</script>

<template>
  <div class="w-[496px] rounded-lg bg-white p-6 shadow-md">
    <template v-if="isLoading">
      <v-skeleton-loader boilerplate type="subtitle, paragraph" />
    </template>
    <template v-else>
      <div class="flex flex-col space-y-2" v-if="isNotSourcedByAiRecruiter">
        <span class="text-md font-bold">This application wasn't sourced by your AI recruiter.</span>
        <span class="text-sm text-shade-800"
          >Activate this job to have your AI recruiter score future applications.</span
        >
      </div>
      <div class="flex flex-col gap-y-4" v-else>
        <div class="text-2xl font-bold">
          <div v-if="isNotScored">
            <span>Not scored</span>
          </div>
          <div class="flex gap-x-2" v-else>
            <span>{{ normalizedScore }}</span>
            <score-rating
              v-if="hasScore"
              :key="scoreDetails?.jobApplicationId"
              :score="scoreValue as number"
              :width="16"
              :height="16"
            >
            </score-rating>
          </div>
        </div>
        <div class="flex flex items-start gap-1 self-stretch rounded-md bg-tint-40 p-1">
          <img src="@/assets/svg/conversations/briefcase.svg" alt="Briefcase" />
          <span class="text-sm font-normal text-shade-800">
            {{ jobTitle }} <span v-if="jobId">[{{ jobId }}]</span>
          </span>
        </div>
        <div>
          <div
            class="score-summary text-sm text-shade-880"
            :class="{
              'text-shade-800': isLegacyScore || isNotScored || hasLoadError,
              'ml-4': scoreVersion === 'v2' && hasSummary,
            }"
            data-test="application-score-blurb-summary"
            v-html="summaryHtml"
          ></div>
        </div>
        <div v-if="hasScore && chips.length" class="flex flex-col gap-2">
          <div class="flex flex-wrap gap-2">
            <ScoreChip
              v-for="chip in chips"
              :key="chip.title"
              :chipTitle="chip.title"
              :chipText="chip.text"
              :color="chip.color"
              :should-show-dialog="true"
              :analytics-source="analyticsSource"
              :application-id="applicationId"
              :score="scoreValue as number"
            />
          </div>

          <CopilotScoreFeedback
            v-if="userProfileId && !isLegacyScore && !hasLoadError"
            :job-applicant-id="applicationId"
            :user-profile-id="userProfileId"
            :copilot-score="scoreValue as number"
            :copilot-blurb="summaryText"
            :copilot-chips="chips"
          />
        </div>
        <div v-else-if="!isLegacyScore && !isNotScored && !hasLoadError">
          <p
            class="text-[10px] font-normal italic leading-[21px] tracking-[0.015px] text-shade-800"
          >
            This candidate was scored before the release of our updated scoring system.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.score-summary :deep(ul) {
  @apply !list-disc;
}
</style>
